<script lang="ts">
import type { Color } from 'nuxt-base/components/UiColorBubble.vue'
import { useReturnBoxCtx } from '@/components/ReturnProductBox.vue'

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function createReturnBoxCtx() {
  const t2 = useT2({
    'Please confirm that the items are not used or damaged in any other way.': 'Prašome patvirtinti, kad prekės nėra naudotos ar kitaip sugadintos.',
    'Return Canceled': 'Grąžinimas atšauktas',
    RAN: 'RAN',
    Status: 'Būsena',
    'Created at': 'Sukurta',
    Order: 'Užsakymas',
    'Item returned': 'Grąžinama prekė',
    'Items returned': 'Grąžinamos prekės',
    'Total refund': 'Bendra grąžinimo suma',
    Canceled: 'Atšaukta',
    'Canceled by buyer': 'Atšaukta pirkėjo',
    Created: 'Sukurta',
    'Shipped by buyer': 'Išsiųsta pirkėjo',
    Received: 'Gauta',
    'Refund Issued': 'Pinigai grąžinti',
    'Return Accepted': 'Grąžinimas priimtas',
    Reason: 'Priežastis',
    Quantity: 'Kiekis',
    'Refund amount': 'Grąžinama suma',
    Comment: 'Komentaras',
    'Returned products': 'Grąžinamos prekės',
    'Return bank account': 'Banko sąskaita grąžinimui',
    'Customer contacts': 'Pirkėjo kontaktai',
    'Order ID': 'Užsakymo numeris',
    Email: 'El. paštas',
    Tel: 'Tel.',
    'Return summary': 'Grąžinimo suvestinė',
    'Proceed with the return': 'Tęsti grąžinimą',
    'Return this order': 'Grąžinti užsakymą',
    'Return not allowed for this order': 'Šio užsakymo grąžinti negalima',
    'Sign in': 'Prisijunkite',
    'Create a Return': 'Sukurti grąžinimą',
    'CREATE A RETURN REQUEST': 'SUKURKITE GRĄŽINIMĄ',
    'Select products from your order that you wish to return': 'Pasirinkite prekes ir kiekius, kuriuos norite grąžinti',
    'Select all the product from the order you want to return': 'Pasirinkite prekes ir kiekius, kuriuos norite grąžinti',
    'PREPARE THE PACKAGE': 'PARUOŠKITE SIUNTĄ',
    'Pack the items securely in a suitable box or package': 'Saugiai supakuokite prekes į tinkamą dėžę',
    'SHIP THE PACKAGE': 'IŠSIŲSKITE SIUNTĄ',
    'Send the package to our return address': 'Išsiųskite siuntą nurodytu adresu',
    'AWAIT CONFIRMATION': 'LAUKITE PATVIRTINIMO',
    "Once we receive and inspect your return, we'll notify you via email and process your refund": 'Gavę ir patikrinę grąžinimą, informuosime el. paštu ir atliksime pinigų grąžinimą',
    'Retrieve your order': 'Pasirinkite užsakymą',
    'Choose items to return': 'Pasirinkite grąžinamas prekes',
    'item selected': 'prekė pasirinkta',
    'items selected': 'prekės pasirinktos',
    'Enter your bank account details': 'Iveskite savo banko saskaitą',
    'Confirm your return': 'Patvirtinkite grąžinimą',
    'Request refund': 'Užregistruoti grąžinimą',
    'What to do next': 'Ką daryti toliau',
    'Pack Your Items': 'Supakuokite prekes',
    'Pack all return items securely': 'Saugiai supakuokite visas grąžinamas prekes',
    'If possible, use original packaging to reduce waste': 'Jei įmanoma, naudokite originalią pakuotę',
    'Double-check nothing is missing': 'Patikrinkite, ar nieko netrūksta',
    'Ship to Return Address': 'Siuntimo adresas',
    'Shipping Requirements': 'Siuntimo reikalavimai',
    'Important Information': 'Svarbi informacija',
    'Send package with a trackable, insured service': 'Siųskite siuntą su sekimu ir draudimu',
    'Keep your proof of postage until you receive confirmation': 'Išsaugokite siuntimo dokumentą iki grąžinimo patvirtinimo',
    'We cannot take responsibility for items damaged or lost in transit': 'Neatsakome už pakelyje pažeistas ar dingusias prekes',
    'You have 30 days to return items from purchase date': 'Prekes galite grąžinti per 14 dienų nuo atsiėmimo',
    'Return processing takes up to 7 business days': 'Pinigų grąžinimo procesas trunka iki 10 darbo dienų',
    'Processing may take longer during holidays': 'Atkreipkite dėmesį, kad švenčių metu procesas gali šiek tiek užtrukti',
    'Your refund will be issued once return is processed': 'Pinigai bus grąžinti, tik tada, kai gausime ir patikrinsime jūsų grąžinamų prekių būklę',
    'Return instructions will also be sent': 'Grąžinimo instrukcijos papildomai bus išsiųstos',
    'Return instructions have been sent to': 'Grąžinimo instrukcijos išsiųstos',
  })

  function createReturnStatusTranslator(): (val: gen.EnumSellerReturnType | None, fb?: string | undefined) => stringI18n {
    return useEnumTranslator<gen.EnumSellerReturnType>(() => ({
      Canceled: t2('Canceled'),
      CanceledByBuyer: t2('Canceled by buyer'),
      Created: t2('Created'),
      ShippedByBuyer: t2('Shipped by buyer'),
      Received: t2('Received'),
      RefundIssued: t2('Refund Issued'),
      Accepted: t2('Return Accepted'),
    }))
  }

  const returnColors: Record<gen.EnumSellerReturnType, Color> = {
    Canceled: 'red',
    CanceledByBuyer: 'grey',
    Created: 'grey',
    Received: 'yellow',
    Accepted: 'green',
    ShippedByBuyer: 'yellow',
  }

  return {
    returnColors,
    createReturnStatusTranslator,
    t2,
  }
}
</script>

<script setup lang="ts">
const props = defineProps<{
  type: 'review' | 'done'
  order: Pick<gen.SellerOrderDocFullFragment, 'uiBox' | 'sellerId' | 'prettyId'> | None
  productCount: number
  totalRefund: string | None
  orderProducts: gen.SellerOrderDocFullFragment['_listingsAll']
  productsInput: gen.InputBuyerReturnProduct[] | None
  products: gen.SellerReturnFullFragment['_products'] | None
  bank: gen.InputSellerPayoutBank | None
  history: gen.SellerReturnFullFragment['history'] | None
}>()

const { t2, returnColors } = createReturnBoxCtx()
const { translateReason } = useReturnBoxCtx().createReturnResonTranslate()
const auth = useAuth()

const translate = useEnumTranslator<gen.EnumSellerReturnType>(() => ({
  Canceled: () => 'Canceled',
  CanceledByBuyer: () => 'Canceled by buyer',
  Created: () => 'Created',
  Received: () => 'Received',
  RefundIssued: () => 'Refund issued',
  Accepted: () => 'Return accepted',
  ShippedByBuyer: () => 'Shipped by buyer',
}))

const listings = useGetListings(() => helpers.arr(props.productsInput, (i) => i.productId), 'skip')

type UiProduct = {
  listing: gen.ProductListingBoxFragment | None
  productId: string
  comment: string | None
  qty: number | None
  reason: string | None
  total: string | None
  cancelReason: string | None
}
const produc = computed((): UiProduct[] => {
  if (props.products?.length) {
    return props.products.map((item): UiProduct => {
      return {
        listing: item.listing,
        productId: item.item.productId,
        comment: item.item.comment,
        reason: item.item.returnReason?.txt.value,
        cancelReason: item.item.cancelReason?.txt.value,
        qty: item.item.qty,
        total: item.total.total?.priceForHumans,
      } satisfies UiProduct
    })
  }
  if (props.productsInput?.length) {
    const orderp = props.orderProducts
    return props.productsInput.map((item): UiProduct => {
      const product = helpers.arrFindId(orderp, item.productId, (i) => i.ref.product.id)
      const priceUnit = product?.ref.priceUnitFinal.number
      return {
        listing: product?.listing || orderp?.find((i) => i.listing?.id === item.productId)?.listing || listings.getOneById(item.productId),
        productId: item.productId,
        comment: item.comment,
        qty: item.qty,
        reason: translateReason(item.reason.code),
        total: priceUnit && item.qty ? helpers.priceFormat(helpers.priceMx(priceUnit, item.qty)) : undefined,
        cancelReason: undefined,
      } satisfies UiProduct
    })
  }

  return []
})
</script>

<template>
  <div>
    <UiLine v-slot="{ emClass }" :label="t2('Order ID')" value="1" mark><UiId :class="emClass" :value="order?.prettyId" /></UiLine>

    <div :class="$style.boxes" class="_pt2">
      <div :class="$style.box">
        <UiUpper class="m_mb1" :title="t2('Customer contacts')" />
        <UiLine :label="undefined" :value="order?.uiBox?.address?._fullname" />
        <UiLine :label="t2('Email')" :value="auth.email" />
        <UiLine :label="t2('Tel')" :value="order?.uiBox?.address?.tel" />
      </div>

      <div :class="$style.box">
        <UiUpper class="m_mb1" :title="t2('Return summary')" />
        <UiLine :label="productCount === 1 ? t2('Item returned') : t2('Items returned')" :value="productCount" />
        <UiLine :label="t2('Total refund')" :value="totalRefund" price />
      </div>

      <div :class="$style.box">
        <UiUpper class="m_mb1" :title="t2('Return bank account')" />
        <ReturnBankUi :item="bank" />
      </div>

      <UiUserActivity v-if="history" :history :ui="(i) => (!i.status ? undefined : { at: i.at, color: returnColors[i.status], label: translate(i.status) })" />
    </div>

    <div>
      <UiUpper class="m_mb1" :title="t2('Returned products')" />
      <div v-if="produc?.length" class="_pb4">
        <div v-for="item in produc" :key="item.productId" :class="$style.products">
          <CartBox :product="undefined" :listing="item.listing" />
          <div>
            <UiLine v-if="item.cancelReason" :label="t2('Return Canceled')" :value="item.cancelReason" color="red" class="_pb2" />
            <UiLine :label="t2('Reason')" :value="item.reason" />
            <UiLine :label="t2('Quantity')" :value="item.qty" />
            <UiLine :label="t2('Refund amount')" :value="item.total" price />
            <UiLine v-if="item.comment" :label="t2('Comment')" :value="item.comment" />
          </div>
        </div>
      </div>
    </div>

    <UiUpper :title="t2('What to do next')" />

    <UiUpper class="_mt4 m_mb1" :title="ts('1.', t2('Pack Your Items'))" />
    <UiLine :label="undefined" :value="t2('Pack all return items securely')" desk />
    <UiLine :label="undefined" :value="t2('If possible, use original packaging to reduce waste')" desk />
    <UiLine :label="undefined" :value="t2('Double-check nothing is missing')" desk />

    <UiUpper class="_mt4 m_mb1" :title="ts('2.', t2('Ship to Return Address'))" />
    <SellerReturnAddress :seller-id="order?.sellerId" :msg="undefined" />

    <UiUpper class="_mt4 m_mb1" :title="ts('3.', t2('Shipping Requirements'))" />
    <UiLine :label="undefined" :value="t2('Send package with a trackable, insured service')" desk />
    <UiLine :label="undefined" :value="t2('Keep your proof of postage until you receive confirmation')" desk />
    <UiLine :label="undefined" :value="t2('We cannot take responsibility for items damaged or lost in transit')" desk />

    <UiUpper class="_mt4 m_mb1" :title="ts('4.', t2('Important Information'))" />
    <UiLine :label="undefined" :value="t2('You have 30 days to return items from purchase date')" desk />
    <UiLine :label="undefined" :value="t2('Return processing takes up to 7 business days')" desk />
    <UiLine :label="undefined" :value="t2('Processing may take longer during holidays')" desk />
    <UiLine :label="undefined" :value="t2('Your refund will be issued once return is processed')" desk />

    <UiLine v-if="type === 'review'" class="_pt4" :label="t2('Return instructions will also be sent')" :value="auth.email" />
    <UiLine v-else class="_pt4" :label="t2('Return instructions have been sent to')" :value="auth.email" />
  </div>
</template>

<style module>
.boxes {
  composes: global(_flexblock);
  composes: global(_wrap);
  gap: 0px 60px;
}
.box {
  composes: global(_wcont);
  composes: global(_pb4);
}

.products {
  composes: global(_w100p);
  composes: global(_gap2);
  composes: global(_mb-1);
  composes: global(_pt1);
  composes: global(_pb1);

  composes: global(_b5);
  composes: global(_bw10);

  composes: global(_grid);

  grid-template: max-content / 50% 50%;
}
:global(.m) .products {
  grid-template: max-content / 1fr;
}
</style>
